import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button/Button';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';

interface ComingSoonPageProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  features?: string[];
  estimatedDate?: string;
}

export function ComingSoonPage({ 
  title, 
  description, 
  icon, 
  features = [], 
  estimatedDate 
}: ComingSoonPageProps) {
  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto text-center">
        {/* Icon */}
        <div className="w-20 h-20 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <div className="text-primary-600">
            {icon}
          </div>
        </div>

        {/* Title and Description */}
        <PageHeader
          title={title}
          description={description}
          className="mb-8"
        />

        {/* Coming Soon Badge */}
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full mb-8">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium">Coming Soon</span>
          {estimatedDate && (
            <>
              <span className="text-primary-400">•</span>
              <span>{estimatedDate}</span>
            </>
          )}
        </div>

        {/* Features Preview */}
        {features.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-4">What to expect:</h3>
            <ul className="space-y-3 text-left">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-success-100 rounded-full flex items-center justify-center mt-0.5 shrink-0">
                    <svg className="w-3 h-3 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/organization">
            <Button variant="outline">
              Back to Overview
            </Button>
          </Link>
          <Button disabled className="opacity-50 cursor-not-allowed">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h9" />
            </svg>
            Get Notified
          </Button>
        </div>

        {/* Additional Info */}
        <div className="mt-12 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            We're working hard to bring you this feature. In the meantime, check out our other 
            organization settings or create some amazing content!
          </p>
        </div>
      </div>
    </div>
  );
}
